import React from "react";
import { useParams, useLocation, Link } from "react-router-dom";
import { Instagram, Mail, MapPin } from "lucide-react";
import { useSEO } from "../lib/useSEO";
export function About() {
  useSEO({ title: "About Us | Joy Pearl", description: "About Joy Pearl — a small Indian boutique curating heirloom-style artificial jewellery and adorable kids' gifts from Gurgaon, Haryana." });
  return (
    <div className="jp-container py-12 md:py-20 max-w-3xl" data-testid="about-page">
      <p className="jp-eyebrow mb-3">About Joy Pearl</p>
      <h1 className="font-serif text-4xl md:text-5xl mb-6">Heirloom-style jewellery for the everyday.</h1>
      <p className="text-brand-ink/85 leading-relaxed mb-4">
        Joy Pearl is a small Indian boutique that curates artificial jewellery and adorable kids' gifts, hand-picked for quality and finish. From classic pearl strands to playful jhumkas, every piece is selected by our in-house team and packed with love in Gurgaon, Haryana.
      </p>
      <p className="text-brand-ink/85 leading-relaxed">
        We believe beautiful things should feel reachable — that's why we keep our pricing honest and offer Cash on Delivery across India.
      </p>
      <h2 className="font-serif text-2xl mt-12 mb-3">The Company</h2>
      <p className="text-brand-ink/85">
        <strong>Vasudevaya Sreekrishna Enterprises</strong><br/>
        GSTIN 06BITPK7883H1ZH<br/>
        Sector 84, Gurgaon, Haryana 122004, India<br/>
        Region of operation: Delhi NCR & India.
      </p>
      <div className="mt-6 flex flex-wrap gap-4 text-sm">
        <a href="https://www.instagram.com/joypearl_official" target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 text-brand-primary hover:underline"><Instagram size={16}/> @joypearl_official</a>
        <Link to="/contact" className="inline-flex items-center gap-2 text-brand-primary hover:underline"><Mail size={16}/> Contact us</Link>
      </div>
    </div>
  );
}

export function Contact() {
  useSEO({ title: "Contact Us | Joy Pearl", description: "Get in touch with Joy Pearl — Instagram @joypearl_official, email joypearlvske@gmail.com. Vasudevaya Sreekrishna Enterprises, Gurgaon, Haryana." });
  return (
    <div className="jp-container py-12 md:py-20 max-w-2xl" data-testid="contact-page">
      <p className="jp-eyebrow mb-3">Contact</p>
      <h1 className="font-serif text-4xl md:text-5xl mb-6">Talk to us.</h1>
      <div className="grid sm:grid-cols-2 gap-6 text-sm">
        <div className="bg-white border border-brand-line p-6">
          <div className="text-brand-primary mb-2"><MapPin size={18}/></div>
          <p className="font-medium">Visit / Mail</p>
          <p className="text-brand-muted mt-1">Sector 84, Gurgaon<br/>Haryana 122004, India</p>
        </div>
        <div className="bg-white border border-brand-line p-6">
          <div className="text-brand-primary mb-2"><Instagram size={18}/></div>
          <p className="font-medium">DM us on Instagram</p>
          <a href="https://www.instagram.com/joypearl_official" target="_blank" rel="noopener noreferrer" className="text-brand-primary hover:underline mt-1 inline-block">@joypearl_official</a>
        </div>
        <div className="bg-white border border-brand-line p-6 sm:col-span-2">
          <div className="text-brand-primary mb-2"><Mail size={18}/></div>
          <p className="font-medium">Email Support</p>
          <a href="mailto:joypearlvske@gmail.com" className="text-brand-primary hover:underline mt-1 inline-block" data-testid="contact-support-email">joypearlvske@gmail.com</a>
          <p className="text-xs text-brand-muted mt-2">For DPDP / privacy concerns: <a href="mailto:grievance@vskstore.com" className="text-brand-primary hover:underline">grievance@vskstore.com</a></p>
        </div>
      </div>
    </div>
  );
}

const LEGAL = {
  privacy: {
    title: "Privacy Policy",
    body: [
      "Joy Pearl (operated by Vasudevaya Sreekrishna Enterprises) respects your privacy. We collect only the personal data needed to process your orders, deliver products to you, and improve our service.",
      "Data we collect: name, email, phone, delivery address, order history, and (if you sign in via Google) your basic profile and avatar. We do not sell or rent your personal data to third parties.",
      "Cookies: we use a small number of cookies for session management (httpOnly, secure) and basic analytics — only with your consent. You can withdraw consent in your Account → Privacy & Your Data section at any time.",
      "Your rights under the DPDP Act 2023: You may request a copy of your data (access), ask us to correct inaccuracies (correction), or request deletion of your account (erasure). You may also raise a grievance. Use the request form on your Account page or write to our Grievance Officer below — we respond within 30 days.",
      "Grievance Officer: Joy Pearl by Vasudevaya Sreekrishna Enterprises, Sector 84, Gurgaon, Haryana 122004, India. Email: grievance@vskstore.com. Instagram DM: @joypearl_official.",
      "Children: Joy Pearl is intended for adults shopping for adults or children. We do not knowingly collect data from minors directly.",
      "Data retention: We retain order records for the period required by Indian tax and consumer protection law (currently 8 years). On erasure requests we anonymize personal fields while retaining minimal transactional records as legally required.",
    ],
  },
  terms: {
    title: "Terms & Conditions",
    body: [
      "By using vskstore.com (Joy Pearl) you agree to these terms. We may update them from time to time; the latest version is always available on this page.",
      "Eligibility: You must be at least 18 years old (or have parental consent) and a resident of India to place orders. Joy Pearl ships only within India.",
      "Products: We sell artificial (imitation) jewellery and kids' gift items. While we curate for quality, minor variations in colour and finish may occur. Product images are representative.",
      "Pricing: All prices are in Indian Rupees (₹) inclusive of GST where applicable. GST is invoiced by Vasudevaya Sreekrishna Enterprises (GSTIN 06BITPK7883H1ZH).",
      "Cash on Delivery (COD): Orders are confirmed via OTP for buyer verification. Refusal to accept delivery after OTP confirmation may attract a small handling fee and the customer may be flagged for future COD eligibility.",
      "Serviceability: We deliver only to pincodes on our serviceable list. Orders to non-serviceable pincodes will not be accepted at checkout.",
      "Disputes are subject to the jurisdiction of courts at Gurgaon, Haryana.",
    ],
  },
  returns: {
    title: "Returns & Refunds",
    body: [
      "We offer a 7-day return window from the date of delivery for unused items in their original packaging.",
      "Hygiene exception: Earrings and personal-care items cannot be returned once unsealed, due to hygiene reasons.",
      "To initiate a return, message us on Instagram @joypearl_official or via the Contact page with your order number and reason.",
      "Refunds are issued to your original payment method within 7 business days of receiving the returned item. For COD orders, refunds are processed via UPI / bank transfer to a verified account.",
      "Shipping charges on returns are non-refundable unless the return is due to a defect or our error.",
    ],
  },
  shipping: {
    title: "Shipping Policy",
    body: [
      "Joy Pearl ships only within India. Orders are typically dispatched in 1-2 business days from Gurgaon, Haryana.",
      "Standard delivery: 3-7 business days to most serviceable pincodes. Delhi NCR usually within 3 days.",
      "Free shipping on orders above ₹499. Below that, a flat ₹49 shipping fee applies.",
      "Cash on Delivery: a small handling fee applies and is shown at checkout. COD is available for orders up to ₹5,000 (admin-configurable).",
      "Pincode serviceability is checked at checkout. If your pincode isn't on our list yet, please DM us on Instagram — we add new pincodes regularly.",
    ],
  },
  dpdp: {
    title: "DPDP Act 2023 — Grievance Redressal",
    body: [
      "Joy Pearl complies with the Digital Personal Data Protection Act, 2023. This page summarises your rights and how to raise a grievance.",
      "Your rights: (1) Right to access — you can download your personal data from Account → Privacy & Your Data. (2) Right to correction — request corrections to inaccurate or incomplete data. (3) Right to erasure — request deletion of your account. (4) Right to grievance — raise concerns about how we handle your data.",
      "How to raise a request: Log into your Account and use the request form, or email our Grievance Officer.",
      "Grievance Officer: Joy Pearl by Vasudevaya Sreekrishna Enterprises. Email: grievance@vskstore.com. Address: Sector 84, Gurgaon, Haryana 122004, India.",
      "We acknowledge requests within 7 days and resolve them within 30 days. If you are not satisfied with our response, you may approach the Data Protection Board of India.",
    ],
  },
};

// Map alias URLs (e.g. /privacy-policy) to the canonical LEGAL key.
const ALIAS_TO_SLUG = {
  "privacy-policy": "privacy",
  "privacy": "privacy",
  "terms": "terms",
  "terms-and-conditions": "terms",
  "shipping-policy": "shipping",
  "shipping": "shipping",
  "returns": "returns",
  "refund-policy": "returns",
  "dpdp": "dpdp",
  "grievance": "dpdp",
};

const LEGAL_SEO = {
  privacy: { title: "Privacy Policy | Joy Pearl", description: "How Joy Pearl collects, uses and protects your personal data under India's DPDP Act 2023." },
  terms: { title: "Terms & Conditions | Joy Pearl", description: "Terms and conditions for using Joy Pearl by Vasudevaya Sreekrishna Enterprises. India-only shopping." },
  shipping: { title: "Shipping & Delivery Policy | Joy Pearl", description: "Joy Pearl shipping policy — dispatch in 1-2 business days from Gurgaon, free shipping over ₹499 across India." },
  returns: { title: "Returns & Refund Policy | Joy Pearl", description: "7-day return window for unused Joy Pearl items in original packaging. Refunds within 7 business days." },
  dpdp: { title: "DPDP Act Compliance | Joy Pearl", description: "Joy Pearl's DPDP Act 2023 compliance — exercise your rights to access, correction and erasure. Grievance officer contact." },
};

export function LegalPage() {
  const params = useParams();
  const location = useLocation();
  // If we arrived via /legal/:slug use params.slug; otherwise resolve from the URL.
  const fromPath = (location.pathname.split("/").filter(Boolean)[0] || "").toLowerCase();
  const slug = params.slug || ALIAS_TO_SLUG[fromPath] || fromPath;
  const page = LEGAL[slug];
  const seo = LEGAL_SEO[slug] || { title: "Legal | Joy Pearl" };
  useSEO(seo);
  if (!page) return <div className="jp-container py-20 text-center"><h1 className="font-serif text-3xl">Page not found</h1></div>;
  return (
    <div className="jp-container py-12 max-w-3xl" data-testid={`legal-${slug}`}>
      <p className="jp-eyebrow mb-3">Legal</p>
      <h1 className="font-serif text-4xl md:text-5xl mb-8">{page.title}</h1>
      <div className="space-y-5 text-brand-ink/85 leading-relaxed">
        {page.body.map((p, i) => <p key={i}>{p}</p>)}
      </div>
      <p className="mt-12 text-sm text-brand-muted">
        Vasudevaya Sreekrishna Enterprises • GSTIN 06BITPK7883H1ZH • Gurgaon, Haryana
      </p>
    </div>
  );
}
